import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useChatStore } from '@/stores/chat-store';
import vtakLogo from '@/assets/vtak-logo.png';
import type { AiConversation } from '@vtak/shared';

/**
 * Scrollable AI conversation list (headerless) shared by the sidebar panel
 * and the floating chat window — each host supplies its own header.
 */
export function AiConversationList({
  onSelect,
  onNew,
}: {
  onSelect: (id: string) => void;
  onNew: () => void;
}): React.JSX.Element {
  const conversations = useChatStore((s) => s.aiConversations);
  const deleteConversation = useChatStore((s) => s.deleteAiConversation);

  async function handleDelete(e: React.MouseEvent, id: string): Promise<void> {
    e.stopPropagation();
    await deleteConversation(id);
  }

  return (
    <ScrollArea className="flex-1 p-2">
      {conversations.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-16">
          <img src={vtakLogo} alt="VTAK AI" className="h-6 w-auto opacity-60" />
          <p className="max-w-[220px] text-center text-xs text-muted-foreground">
            Ask about aircraft, satellites, and anything else on the map.
          </p>
          <Button size="sm" onClick={onNew} className="gap-1.5 bg-ai text-white hover:bg-ai/90">
            <Plus className="h-3.5 w-3.5" />
            Start a conversation
          </Button>
        </div>
      ) : (
        <div className="space-y-0.5">
          {conversations.map((conv) => (
            <ConversationRow
              key={conv.id}
              conversation={conv}
              onSelect={() => onSelect(conv.id)}
              onDelete={(e) => handleDelete(e, conv.id)}
            />
          ))}
        </div>
      )}
    </ScrollArea>
  );
}

/** Single conversation row in the list */
function ConversationRow({
  conversation,
  onSelect,
  onDelete,
}: {
  conversation: AiConversation;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onSelect}
      className="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/[0.06] active:bg-white/[0.10]"
    >
      <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground/60" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-foreground">{conversation.title}</p>
        <p className="font-mono text-[10px] tracking-wide text-muted-foreground/60">
          {formatRelativeTime(conversation.updatedAt)}
        </p>
      </div>
      <button
        onClick={onDelete}
        className="shrink-0 rounded-md p-1.5 text-muted-foreground/40 opacity-0 transition-all hover:bg-destructive/15 hover:text-red-400 group-hover:opacity-100"
        title="Delete conversation"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </button>
  );
}

/** Format a timestamp as relative time */
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}
